import { EstimateResponse } from '@backend/schemas'

type CabasLine = EstimateResponse['cabasLines'][number]
type CabasMaterialLine = Extract<CabasLine, { CurrentListPrice: number }>

// The Swedish label describing which price a cabas line uses. Mirrors the CABAS
// flags: IsUsedPart / EconomyPart / PriceGiven, defaulting to "Listpris".
export type CabasPriceType = 'List' | 'Justerat' | 'Använd' | 'Ekonomi'

export function cabasPriceType(item: CabasMaterialLine): CabasPriceType {
  if (item.IsUsedPart) return 'Använd'
  if (item.EconomyPart) return 'Ekonomi'
  if (item.PriceGiven) return 'Justerat'
  return 'List'
}

type PriceDetails = { value: number; type: CabasPriceType | null }

const round2 = (n: number) => Math.round(n * 100) / 100

// The distinct prices a material line could be billed at (full list vs. discounted),
// each with the label to mark it with.
function priceCandidates(item: CabasMaterialLine): PriceDetails[] {
  const candidates: PriceDetails[] = [
    { value: item.Price, type: cabasPriceType(item) },
    { value: item.EnteredListPrice, type: 'List' },
    { value: item.CurrentListPrice, type: 'List' },
  ]
  return candidates.filter(c => c.value > 0)
}

// The price we resolve for a cabas line when there's no invoice line to compare against.
function defaultPriceDetails(item: CabasLine): PriceDetails {
  // WorkResult
  if (!('CurrentListPrice' in item)) return { value: item.Amount, type: null }

  // Special-price parts (used / economy / manually adjusted) use the actual Price,
  // marked with their type.
  if (item.IsUsedPart || item.EconomyPart || item.PriceGiven) {
    return { value: item.Price, type: cabasPriceType(item) }
  }

  // A genuine entered list price override — set and different from both Price and
  // CurrentListPrice. A value that merely duplicates one of those isn't a real
  // override (Price / CurrentListPrice "wins"), so no marker is shown.
  const entered = item.EnteredListPrice
  if (entered && entered !== item.Price && entered !== item.CurrentListPrice) {
    return { value: entered, type: cabasPriceType(item) }
  }

  // Plain list price: entered when set, else CurrentListPrice, else Price (0 = unset).
  return { value: entered || item.CurrentListPrice || item.Price, type: null }
}

// Resolves the price to display for a cabas line, plus the price type to mark in
// the UI as "[price] (type)" (null = no marker). When matched to an invoice line,
// prefer whichever candidate price matches the invoice (e.g. the full list price
// vs. the discounted price) so an equal deal isn't flagged as an inconsistency.
export function cabasPriceDetails(item: CabasLine, invoicePrice?: number | null): PriceDetails {
  const def = defaultPriceDetails(item)

  if (invoicePrice != null && 'CurrentListPrice' in item && round2(def.value) !== round2(invoicePrice)) {
    const match = priceCandidates(item).find(c => round2(c.value) === round2(invoicePrice))
    if (match) return match
  }

  return def
}

export function cabasPrice(item: CabasLine, invoicePrice?: number | null) {
  return cabasPriceDetails(item, invoicePrice).value
}
